import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { AlertCircle, RotateCcw } from "lucide-react";

export function V2Page({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const content = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = content.current;
    if (!root) return;
    let frame = 0;
    const labelTables = () => {
      root
        .querySelectorAll<HTMLElement>('[data-slot="table-container"]')
        .forEach((region) => {
          region.setAttribute("role", "region");
          region.setAttribute("aria-label", `${title} records`);
          region.tabIndex = 0;
          const table = region.querySelector("table");
          // Editable line-item grids retain horizontal scrolling; record lists restack on phones.
          if (
            !table ||
            table.querySelector(
              'tbody input:not([type="checkbox"]), tbody [role="combobox"]',
            )
          )
            return;
          const heads = Array.from(table.querySelectorAll("thead th"));
          if (heads.length < 2) return;
          table.setAttribute("data-v2-record-list", "");
          table
            .querySelectorAll<HTMLTableRowElement>("tbody tr")
            .forEach((row) => {
              if (
                row.cells.length !== heads.length ||
                Array.from(row.cells).some((cell) => cell.colSpan > 1)
              )
                return;
              row.setAttribute("data-v2-record-row", "");
              Array.from(row.cells).forEach((cell, index) => {
                const heading = heads[index];
                const label = heading?.querySelector('[role="checkbox"]')
                  ? "Select record"
                  : heading?.textContent?.trim() || "Actions";
                cell.setAttribute("data-v2-label", label);
              });
            });
        });
    };
    labelTables();
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(labelTables);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [title]);
  return (
    <section className="v2-page">
      <header className="v2-page-header">
        <div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="v2-page-actions">{actions}</div>}
      </header>
      <div className="v2-page-content" ref={content}>
        {children}
      </div>
    </section>
  );
}

export function V2AsyncState({
  loading,
  error,
  onRetry,
  children,
}: {
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (loading)
    return (
      <div className="v2-loading" role="status" aria-live="polite">
        <span className="v2-sr-only">Loading records…</span>
        <div />
        <div />
        <div />
      </div>
    );
  if (error)
    return (
      <div className="v2-error" role="alert">
        <AlertCircle size={24} aria-hidden="true" />
        <h2>Unable to load this page</h2>
        <p>
          {error instanceof Error
            ? error.message
            : "The request failed. Please try again."}
        </p>
        {onRetry && (
          <button type="button" className="v2-button" onClick={onRetry}>
            <RotateCcw size={15} aria-hidden="true" />
            Try again
          </button>
        )}
      </div>
    );
  return <>{children}</>;
}
